import type { RewardItem } from "./reward";

/**
 * 每日奖励（登录签到）配置。两层：
 * - 周期档 days：第 1…N 天各发什么。行数即周期长度，代码不写死 7。
 * - 累计档 milestones：当月累计领取天数到 seq 时加发一份（进度条上的礼盒）。
 *
 * 奖励项与通关奖励同形状（RewardItem），编辑复用同一个弹窗——不另造一套奖励词汇。
 * 规则口径（错过不重置、领满回到第 1 天、跨月清零）在服务端 schema/daily-reward.ts，这里只管编辑。
 */
export interface DailySlot {
  /** day：周期内第几天（1 起连续）；milestone：当月累计天数阈值（1~31） */
  seq: number;
  items: RewardItem[];
}
export interface DailyRewards {
  days: DailySlot[];
  milestones: DailySlot[];
}

export const EMPTY_DAILY: DailyRewards = { days: [], milestones: [] };

/** 累计档阈值上限：一个自然月最多 31 天，超过永远到不了（发布闸 DAILY_MILESTONE_RANGE） */
export const MILESTONE_MAX = 31;

export const sortSlots = (xs: DailySlot[]): DailySlot[] => [...xs].sort((a, b) => a.seq - b.seq);

/** 新增一天：接在最后一天后面，奖励待填 */
export const newDay = (days: DailySlot[]): DailySlot => ({
  seq: days.reduce((m, d) => Math.max(m, d.seq), 0) + 1,
  items: [{ kind: "spin", qty: null }],
});

/** 新增一档：取比现有最大阈值大 7 的数（8 / 15 / 22 / 30 那种节奏），封顶 31 */
export const newMilestone = (ms: DailySlot[]): DailySlot => {
  const max = ms.reduce((m, d) => Math.max(m, d.seq), 0);
  let seq = Math.min(MILESTONE_MAX, max ? max + 7 : 8);
  while (ms.some((m) => m.seq === seq) && seq > 1) seq--;
  return { seq, items: [{ kind: "spin", qty: null }] };
};

/** 周期档断在第几天（第一个不连续的位置），null = 连续 */
export function dayGapOf(days: DailySlot[]): number | null {
  const sorted = sortSlots(days);
  for (let i = 0; i < sorted.length; i++) if (sorted[i].seq !== i + 1) return i + 1;
  return null;
}

/** 某一档的问题：空奖励 / 累计阈值越界 / 阈值重复 */
export type SlotIssue = "dr.issue.empty" | "dr.issue.range" | "dr.issue.dup";
export function slotIssue(slot: DailySlot, kind: "day" | "milestone", all: DailySlot[]): SlotIssue | null {
  if (!slot.items.length) return "dr.issue.empty";
  if (kind === "milestone" && (slot.seq < 1 || slot.seq > MILESTONE_MAX)) return "dr.issue.range";
  if (all.filter((x) => x.seq === slot.seq).length > 1) return "dr.issue.dup";
  return null;
}
